import { Magnifier } from "@wit/mpesa-ui-components";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ReactSortable } from "react-sortablejs";
import styled, { ThemeContext } from "styled-components";
import { IStoreInterface } from "../../../../configs/store.config";
import { ListHeader, Row } from "../../../../shared/shared.styled";
import "../product-recommender.css";
import { IProductRecommendation } from "../product-recommender.model";
import ProductRecommenderItem from "./product-recommender-item.component";
import { isEqual } from "lodash";
import { getAvailableLanguagesOptions } from "../product-recommender.utils";
import ReactTooltip from "react-tooltip";
import LanguageSelectorDropdown from "../../../../shared/components/language-selector-dropdown.component";

export interface IProductRecommendationsListInterface {
  productRecommendations: IProductRecommendation[];
  setProductRecommendations: (newState: IProductRecommendation[]) => void;
  onLanguageChange: (language: { label: string; key: string }) => void;
  selectedLanguage: { label: string; key: string };
  onDelete: (id: string) => void;
  hasSubmittedVersion?: boolean;
}

/**
 * Product Recommendations list
 * @param param0
 */
const ProductRecommendationsList = ({
  productRecommendations,
  setProductRecommendations,
  onLanguageChange,
  selectedLanguage,
  onDelete,
  hasSubmittedVersion = false,
}: IProductRecommendationsListInterface) => {
  const [t] = useTranslation();
  const productRecommendationsStore = useSelector(
    (state: IStoreInterface) => state.productRecommendationsReducer.productRecommendations,
  );
  const [dragging, setDragging] = useState(false);
  const theme = useContext(ThemeContext);

  /**
   * Gets the default translation for a given field
   * @param obj
   */
  const getObjectTranslation = (obj: { [x: string]: any }) => {
    return obj !== null ? (obj[selectedLanguage.key] ? obj[selectedLanguage.key] : "n.a.") : "n.a";
  };

  /**
   * Move handler
   * @param event
   */
  const onMoveHandler = (event: any) => {
    if (
      productRecommendationsStore &&
      productRecommendationsStore.productRecommendations &&
      productRecommendations &&
      productRecommendationsStore.productRecommendations.length === productRecommendations.length
    ) {
      return event;
    } else {
    }
  };

  /**
   * list equal
   * @param oldValues
   * @param newValues
   */
  const isListEqual = (oldValues: any[], newValues: any[]) => {
    if (!oldValues.length) {
      return true;
    }

    if (!oldValues[0].chosen) {
      oldValues = oldValues.map(v => {
        return { ...v, chosen: false, selected: false };
      });
    }
    return isEqual(oldValues, newValues);
  };

  return (
    <div>
      <ListHeaders>
        <ListHeader ratio={2 / 12} style={{ marginRight: 16 }}>
          {productRecommendations && productRecommendations ? (
            <LanguageSelectorDropdown
              availableLanguages={getAvailableLanguagesOptions()}
              selectedLanguage={selectedLanguage}
              changeSelectedLanguage={onLanguageChange}
              color={theme.palette.turquoiseBlue}
              id={"language-selector"}
            ></LanguageSelectorDropdown>
          ) : null}
        </ListHeader>
        <ListHeader ratio={3 / 12} style={{ marginRight: 16 }}>
          {t("pages.productRecommendations.title")}
        </ListHeader>
        <ListHeader ratio={4 / 12} style={{ marginRight: 16 }}>
          {t("pages.productRecommendations.description")}
        </ListHeader>
      </ListHeaders>
      {productRecommendations ? (
        <>
          <ReactSortable
            ghostClass={"sortable-ghost"}
            chosenClass={"sortable-chosen"}
            dragClass={"sortable-drag"}
            list={productRecommendations}
            handle={".handle"}
            setList={(newState, sortable, store) => {
              if (!isListEqual(productRecommendations, newState)) {
                setProductRecommendations(newState);
              }
            }}
            onMove={onMoveHandler}
            onChoose={() => {
              setDragging(true);
            }}
            onUnchoose={() => {
              setDragging(false);
              ReactTooltip.hide();
            }}
          >
            {productRecommendations.map(recommendation => {
              return (
                <ProductRecommenderItem
                  key={recommendation.id}
                  productRecommendation={recommendation}
                  getTranslation={getObjectTranslation}
                  onDelete={onDelete}
                  hasFastActions={!hasSubmittedVersion}
                  dragging={dragging}
                />
              );
            })}
          </ReactSortable>
        </>
      ) : null}
      {productRecommendations.length === 0 ? (
        <MagnifierContainer>
          <Magnifier />
          <NoResultsFoundText>{t("pages.productRecommendations.noSearchResults")}</NoResultsFoundText>
        </MagnifierContainer>
      ) : null}
    </div>
  );
};

const ListHeaders = styled(Row)`
  padding: 0px;
  margin-bottom: 15px;
`;

const MagnifierContainer = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  margin-top: 50px;
  svg {
    width: 140px;
    height: 140px;
    margin-bottom: 24px;
    stroke: red;
  }
`;

const NoResultsFoundText = styled("span")`
  font-family: Vodafone Rg;
  font-size: 22px;
  color: ${props => props.theme.palette.midGrey};
`;

export default ProductRecommendationsList;
